import { createSelector } from 'reselect'

import {
  getAgentUsernameOrYou,
  getAgentPossessiveUsernameOrMine,
  getAgentUsername,
} from 'util/agents'
import { filter, mapObject, emptyObj } from 'util/objects'
import { objectHashSerializer, memoize } from 'util/memoization'
import { emptyArr } from 'util/arrays'
import { CHAT_ADMIN_NAME } from 'ducks/chat/utils/users'

// eslint-disable-next-line no-underscore-dangle
const _selectByIdIncArchived = state => state.agents.byId || {}

export const selectRawAgentsById = state => state.agents.byId || {}

export function selectAgent(state, id) {
  return selectRawAgentsById(state)[id] || emptyObj
}

// eslint-disable-next-line no-underscore-dangle
const _selectById = createSelector(_selectByIdIncArchived, agents => {
  return filter(agent => !agent.archived, agents)
})

// NOTE (jscheel): Private versions so we don't create a dependency. Lame.
// eslint-disable-next-line no-underscore-dangle
const _selectCurrentUser = state => state.currentUser
// eslint-disable-next-line no-underscore-dangle
const _selectGroups = state => state.groups || emptyArr

const decorate = memoize(
  (currentUser, groups) => agent => {
    const decoratedAgent = Object.assign({}, agent)
    decoratedAgent.label = getAgentUsernameOrYou(decoratedAgent, currentUser)
    decoratedAgent.possessiveLabel = getAgentPossessiveUsernameOrMine(
      decoratedAgent,
      currentUser
    )
    decoratedAgent.ticketSearchQuery = `assignee:${getAgentUsername(agent)}`
    decoratedAgent.groups = groups.filter(group => {
      return (
        group.agents.findIndex(({ id: agentId }) => agentId === agent.id) > -1
      )
    })
    return decoratedAgent
  },
  {
    serializer: objectHashSerializer,
  }
)

export const selectAgentsById = createSelector(
  _selectById,
  _selectCurrentUser,
  _selectGroups,
  (byId, currentUser, groups) =>
    mapObject(decorate(currentUser, groups))(byId || {})
)

export const selectAgentsByIdIncludingArchived = createSelector(
  _selectByIdIncArchived,
  _selectCurrentUser,
  _selectGroups,
  (byId, currentUser, groups) =>
    mapObject(decorate(currentUser, groups))(byId || {})
)

export const selectAgents = createSelector(selectAgentsById, byId =>
  Object.values(byId)
)

export const selectAdminAgents = createSelector(selectAgents, agents =>
  agents.filter(agent => agent.role === 'admin' || agent.role === 'owner')
)

export const selectAgentIncludeGroups = createSelector(
  selectAgents,
  _selectGroups,
  (agents, groups) => {
    return agents.map(agent => ({
      ...agent,
      groups: groups.filter(g => g.agents.some(a => a.id === agent.id)),
    }))
  }
)

export const selectAgentCount = createSelector(
  selectAgents,
  agents => agents.length
)

export const selectCurrentAgent = createSelector(
  selectAgentsById,
  _selectCurrentUser,
  (agentsById, currentUser) => agentsById[currentUser.id]
)

export const selectAgentsByUsername = createSelector(selectAgents, agents => {
  return agents.reduce((hash, agent) => {
    hash[getAgentUsername(agent)] = agent // eslint-disable-line no-param-reassign
    return hash
  }, {})
})

const makeSelectAgentsSortedBy = sortBy => {
  return createSelector(
    selectAgents,
    _selectCurrentUser,
    (agents, currentUser) => {
      return [].concat(agents).sort((a, b) => {
        if (a.id === currentUser.id) {
          return -1
        } else if (b.id === currentUser.id) {
          return 1
        }
        // eslint-disable-next-line no-nested-ternary
        return a[sortBy] < b[sortBy] ? -1 : a[sortBy] > b[sortBy] ? 1 : 0
      })
    }
  )
}

export const selectAgentsSortedByUsername = makeSelectAgentsSortedBy('username')
export const selectAgentsSortedByName = makeSelectAgentsSortedBy('name')

export const selectRawAgents = createSelector(selectRawAgentsById, byId =>
  Object.values(byId)
)

export function selectMxEventFromProps(_, props) {
  return props.mxEvent || null
}

export function makeSelectAgentUsernameFromMxEvent() {
  return createSelector(
    selectRawAgentsById,
    _selectCurrentUser,
    selectMxEventFromProps,
    (agentsById, currentUser, mxEvent) => {
      if (!mxEvent) return null
      const senderId = mxEvent.getSender()
      if (senderId.startsWith('@chat-admin')) return CHAT_ADMIN_NAME
      const agent = Object.values(agentsById).find(a => a.chatId === senderId)
      if (!agent) return null
      return getAgentUsernameOrYou(agent, currentUser, agentsById)
    }
  )
}
